/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Typography, Typography2, breakpoints } from '@coursera/cds-core';

import Breadcrumbs from 'bundles/shared-articles/components/ArticleBreadcrumbs';
import { getImageAspectRatio } from 'bundles/shared-articles/utils/images';
import type { Asset, Maybe } from 'bundles/tutorials/__generated__/baseContentfulTypes';
import Tag from 'bundles/tutorials/components/Tag';
import TutorialByLine from 'bundles/tutorials/components/TutorialByLine';
import { FEATURED_IMAGE_DIMENSIONS } from 'bundles/unified-seo-content-common/constants';

import _t from 'i18n!nls/tutorials';

type TutorialHeaderProps = {
  title: string;
  subtitle?: string;
  featuredImage?: Partial<Asset> | null;
  featuredImageCaption?: string | null;
  latestPublishedDate: string;
  authorNames: string;
  tags?: Maybe<Maybe<string>[]>;
};

const styles = {
  contentContainer: css`
    position: relative;
  `,
  title: css`
    margin-bottom: 5px;
    position: relative;
  `,
  tags: css`
    list-style: none;
    margin-bottom: 15px;
  `,
  headerByLine: () => css`
    margin-bottom: 1rem;
    ${breakpoints.down('sm')} {
      max-width: 100%;
    }
  `,
  subtitle: css`
    font-size: 1.125rem;
    line-height: 1.875rem;
    color: #002d72;
    margin-bottom: 1.5rem;
  `,
  featuredImageContainer: () => css`
    width: 100%;
    ${breakpoints.down('sm')} {
      width: unset;
      margin-left: -16px;
      margin-right: -16px;
    }
  `,
  featuredImageContainerMargin: css`
    margin-bottom: 3rem;
  `,
  imageWrapper: css`
    height: 0;
    overflow: hidden;
    padding-bottom: ${getImageAspectRatio(FEATURED_IMAGE_DIMENSIONS.height, FEATURED_IMAGE_DIMENSIONS.width)}%;
    width: 100%;
  `,
  featuredImage: css`
    max-height: ${FEATURED_IMAGE_DIMENSIONS.height};
    max-width: ${FEATURED_IMAGE_DIMENSIONS.width};
    width: 100%;
  `,
  featuredImageCaption: () => css`
    color: #616161;
    font-weight: 400px;
    font-size: 16px;
    line-height: 24px;
    margin-top: 1.5rem;
    ${breakpoints.down('sm')} {
      margin-left: 16px;
      margin-right: 16px;
    }
  `,
  horizontalRule: css`
    border-style: solid;
    border-color: #bdbdbd;
    margin: 1.5rem 0;
  `,
};

const Header = ({
  title,
  subtitle,
  authorNames,
  featuredImage,
  featuredImageCaption,
  latestPublishedDate,
  tags,
}: TutorialHeaderProps) => {
  return (
    <div className="rc-TutorialHeader">
      <div css={styles.contentContainer}>
        <Breadcrumbs title={title} links={[{ href: '/tutorials', label: _t('Tutorials') }]} />
        <Typography component="h1" variant="d2semibold" css={styles.title} data-e2e="tutorial-page-title">
          {title}
        </Typography>
        {tags?.length && <li css={styles.tags}>{tags.map((tag) => tag && <Tag key={tag} tag={tag} />)}</li>}
        <div css={styles.headerByLine}>
          <TutorialByLine authorNames={authorNames} date={latestPublishedDate} title={title} />
        </div>
        {subtitle && (
          <Typography variant="h2" color="highlightBlue" component="p" css={styles.subtitle}>
            {subtitle}
          </Typography>
        )}
      </div>
      {featuredImage && featuredImage.url ? (
        <figure
          css={[styles.featuredImageContainer, styles.featuredImageContainerMargin]}
          data-e2e="tutorial-page-featured-image"
        >
          <div css={styles.imageWrapper}>
            <ImgixWithPrioritizedLoading
              {...css(styles.featuredImage)}
              src={featuredImage.url}
              alt={featuredImage.description || _t('Tutorial Featured Image')}
              // Image still appears full-width due to styles applied, this will generate a smaller image from Imgix.
              maxWidth={Math.floor(FEATURED_IMAGE_DIMENSIONS.width / 1.5)}
              width="100%"
            />
          </div>
          {featuredImageCaption && (
            <Typography2 component="figcaption" variant="bodySecondary" css={styles.featuredImageCaption}>
              {featuredImageCaption}
            </Typography2>
          )}
        </figure>
      ) : (
        <hr css={styles.horizontalRule} />
      )}
    </div>
  );
};

export default Header;
